import {
  ICeleb,
  IComparisonReportData,
  ILookalikeReportData,
} from '@api/requests';

export function setParamsToSessionStorage(params: Record<string, any>) {
  Object.entries(params).forEach(([key, value]) => {
    window.sessionStorage.setItem(`__${key}`, value);
  });
}

export function getParamsFromSessionStorage(params: Array<string>) {
  const result: Record<string, any> = {};

  params.forEach(key => {
    const value = window.sessionStorage.getItem('__' + key);

    if (value) {
      result[key] = value;
    }
  });

  return result;
}

const SELFIE_STORAGE_KEY = '__selfie';
const COMPARE_IMAGE_STORAGE_KEY = '__compare_image';
const COMPARE_REQUEST_ID_STORAGE_KEY = '__compare_request_id';
const SELECTED_COMPARISON_STORAGE_KEY = '__selected_comparison';
const COMPARISON_REPORT_ID_STORAGE_KEY = '__comparison_report_id';
const COMPARISON_REPORT_DATA_STORAGE_KEY = '__comparison_report_data';
const NAME_STORAGE_KEY = '__name';
const COMPARE_IMAGE_INFO_STORAGE_KEY = '__compare_image_info';
const LOOKALIKE_REPORT_ID_STORAGE_KEY = '__lookalike_report_id';
const LOOKALIKE_REPORT_DATA_STORAGE_KEY = '__lookalike_report_data';

// FaceCompare localStorage keys
const FACE_COMPARE_SELFIE_KEY = '__face_compare_selfie';
const FACE_COMPARE_CELEBRITY_KEY = '__face_compare_celebrity';

export function saveSelfieToSessionStorage(selfie: string | null) {
  if (selfie) {
    window.sessionStorage.setItem(SELFIE_STORAGE_KEY, selfie);
  } else {
    window.sessionStorage.removeItem(SELFIE_STORAGE_KEY);
  }
}

export function getSelfieFromSessionStorage(): string | null {
  return window.sessionStorage.getItem(SELFIE_STORAGE_KEY);
}

export function saveCompareImageToSessionStorage(compareImage: string | null) {
  if (compareImage) {
    window.sessionStorage.setItem(COMPARE_IMAGE_STORAGE_KEY, compareImage);
  } else {
    window.sessionStorage.removeItem(COMPARE_IMAGE_STORAGE_KEY);
  }
}

export function getCompareImageFromSessionStorage(): string | null {
  return window.sessionStorage.getItem(COMPARE_IMAGE_STORAGE_KEY);
}

export function getSelectedComparisonFromSessionStorage(): string | null {
  return window.sessionStorage.getItem(SELECTED_COMPARISON_STORAGE_KEY);
}

export function getReportIdFromSessionStorage(): string | null {
  return window.sessionStorage.getItem(COMPARISON_REPORT_ID_STORAGE_KEY);
}

export function getReportDataFromSessionStorage(): IComparisonReportData | null {
  const reportData = window.sessionStorage.getItem(
    COMPARISON_REPORT_DATA_STORAGE_KEY,
  );
  if (reportData) {
    return JSON.parse(reportData);
  }
  return null;
}

export function saveReportDataToSessionStorage(
  reportData: IComparisonReportData | null,
) {
  if (reportData) {
    window.sessionStorage.setItem(
      COMPARISON_REPORT_DATA_STORAGE_KEY,
      JSON.stringify(reportData),
    );
  } else {
    window.sessionStorage.removeItem(COMPARISON_REPORT_DATA_STORAGE_KEY);
  }
}

export function saveComparisonReportIdToSessionStorage(
  compareImage: string | null,
) {
  if (compareImage) {
    window.sessionStorage.setItem(
      COMPARISON_REPORT_ID_STORAGE_KEY,
      compareImage,
    );
  } else {
    window.sessionStorage.removeItem(COMPARISON_REPORT_ID_STORAGE_KEY);
  }
}

export function saveSelectedComparisonToSessionStorage(
  compareImage: string | null,
) {
  if (compareImage) {
    window.sessionStorage.setItem(
      SELECTED_COMPARISON_STORAGE_KEY,
      compareImage,
    );
  } else {
    window.sessionStorage.removeItem(SELECTED_COMPARISON_STORAGE_KEY);
  }
}

export function getCompareRequestIdFromSessionStorage(): string {
  // #
  const saved = window.sessionStorage.getItem(COMPARE_REQUEST_ID_STORAGE_KEY);
  if (saved) return saved;
  const newId = crypto.randomUUID();
  window.sessionStorage.setItem(COMPARE_REQUEST_ID_STORAGE_KEY, newId);
  return newId;
}

export function getUserNameFromSessionStorage(): string | null {
  return window.sessionStorage.getItem(NAME_STORAGE_KEY);
}

export function saveUserNameToSessionStorage(name: string | null) {
  if (name) {
    window.sessionStorage.setItem(NAME_STORAGE_KEY, name);
  } else {
    window.sessionStorage.removeItem(NAME_STORAGE_KEY);
  }
}

// FaceCompare localStorage functions
export function saveFaceCompareSelfieToLocalStorage(selfie: string | null) {
  if (selfie) {
    window.localStorage.setItem(FACE_COMPARE_SELFIE_KEY, selfie);
  } else {
    window.localStorage.removeItem(FACE_COMPARE_SELFIE_KEY);
  }
}

export function getFaceCompareSelfieFromLocalStorage(): string | null {
  return window.localStorage.getItem(FACE_COMPARE_SELFIE_KEY);
}

export function saveFaceCompareCelebrityToLocalStorage(celebrity: string | null) {
  if (celebrity) {
    window.localStorage.setItem(FACE_COMPARE_CELEBRITY_KEY, celebrity);
  } else {
    window.localStorage.removeItem(FACE_COMPARE_CELEBRITY_KEY);
  }
}

export function getFaceCompareCelebrityFromLocalStorage(): string | null {
  return window.localStorage.getItem(FACE_COMPARE_CELEBRITY_KEY);
}
export function getCompareImageInfoFromSessionStorage(): ICeleb | null {
  const saved = window.sessionStorage.getItem(COMPARE_IMAGE_INFO_STORAGE_KEY);
  return saved ? JSON.parse(saved) : null;
}

export function saveCompareImageInfoToSessionStorage(
  compareImageInfo: ICeleb | null,
) {
  if (compareImageInfo) {
    window.sessionStorage.setItem(
      COMPARE_IMAGE_INFO_STORAGE_KEY,
      JSON.stringify(compareImageInfo),
    );
  } else {
    window.sessionStorage.removeItem(COMPARE_IMAGE_INFO_STORAGE_KEY);
  }
}
export function saveLookalikeReportDataToSessionStorage(
  reportData: ILookalikeReportData | null,
) {
  if (reportData) {
    window.sessionStorage.setItem(
      LOOKALIKE_REPORT_DATA_STORAGE_KEY,
      JSON.stringify(reportData),
    );
  } else {
    window.sessionStorage.removeItem(LOOKALIKE_REPORT_DATA_STORAGE_KEY);
  }
}

export function saveLookalikeReportIdToSessionStorage(
  compareImage: string | null,
) {
  if (compareImage) {
    window.sessionStorage.setItem(
      LOOKALIKE_REPORT_ID_STORAGE_KEY,
      compareImage,
    );
  } else {
    window.sessionStorage.removeItem(LOOKALIKE_REPORT_ID_STORAGE_KEY);
  }
}

export function getLookalikeReportIdFromSessionStorage(): string | null {
  return window.sessionStorage.getItem(LOOKALIKE_REPORT_ID_STORAGE_KEY);
}

export function getLookalikeReportDataFromSessionStorage(): ILookalikeReportData | null {
  const reportData = window.sessionStorage.getItem(
    LOOKALIKE_REPORT_DATA_STORAGE_KEY,
  );
  if (reportData) {
    return JSON.parse(reportData);
  }
  return null;
}
